import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Save } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentRole] = useState(localStorage.getItem('role'));
  
  const [formData, setFormData] = useState({
    contact_form_email: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: ''
  });

  useEffect(() => {
    if (['managing_director', 'administration_manager'].includes(currentRole)) {
      fetchSettings();
    }
  }, [currentRole]);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(
        `${API}/settings/email`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Email settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!['managing_director', 'administration_manager'].includes(currentRole)) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8">
            <CardContent className="text-center">
              <Mail className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Restricted Access</h2>
              <p className="text-gray-400">You need Managing Director or Administration Manager privileges to manage settings.</p>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="settings-page" className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Settings</h1>
          <p className="text-gray-400">Configure email notifications and system settings</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Loading settings...</p>
          </div>
        ) : (
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-500" />
                Email Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-white/10 pb-2">
                    Contact Form Notifications
                  </h3>
                  
                  <div>
                    <Label htmlFor="contact_form_email" className="text-gray-300">
                      Notification Email Address *
                    </Label>
                    <Input
                      id="contact_form_email"
                      data-testid="contact-email-input"
                      type="email"
                      value={formData.contact_form_email}
                      onChange={(e) => setFormData({ ...formData, contact_form_email: e.target.value })}
                      required
                      placeholder="admin@mertek.com"
                      className="bg-white/10 border-white/20 text-white"
                    />
                    <p className="text-sm text-gray-400 mt-1">
                      Contact form submissions will be forwarded to this email address
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-white/10 pb-2">
                    SMTP Server Settings (Optional)
                  </h3>
                  <p className="text-sm text-gray-400">
                    Configure SMTP to enable email sending. Leave blank to disable email notifications.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtp_host" className="text-gray-300">SMTP Host</Label>
                      <Input
                        id="smtp_host"
                        data-testid="smtp-host-input"
                        value={formData.smtp_host}
                        onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                        placeholder="smtp.gmail.com"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_port" className="text-gray-300">SMTP Port</Label>
                      <Input
                        id="smtp_port"
                        data-testid="smtp-port-input"
                        type="number"
                        value={formData.smtp_port}
                        onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                        placeholder="587"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_username" className="text-gray-300">SMTP Username</Label>
                      <Input
                        id="smtp_username"
                        data-testid="smtp-username-input"
                        value={formData.smtp_username}
                        onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                        placeholder="your-email@gmail.com"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="smtp_password" className="text-gray-300">SMTP Password</Label>
                      <Input
                        id="smtp_password"
                        data-testid="smtp-password-input"
                        type="password"
                        value={formData.smtp_password}
                        onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                        placeholder="••••••••"
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        For Gmail, use an App Password instead of your regular password
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    data-testid="save-settings-btn"
                    disabled={isSaving}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
